import { initializeApp, deleteApp } from 'firebase/app';
import { createUserWithEmailAndPassword, deleteUser, getAuth, signOut } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where, writeBatch } from 'firebase/firestore';
import { app, auth, db } from '../../config/firebase';

const EMPLOYEES = 'platformEmployees';
const ROLES = 'platformEmployeeRoles';
const DEPARTMENTS = 'platformDepartments';

const normalizeRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');
const cleanString = (value) => String(value || '').trim();

const ensureAdmin = async () => {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Super Admin authentication is required.');
  const snap = await getDoc(doc(db, 'users', uid));
  const data = snap.exists() ? snap.data() || {} : {};
  if (!['super_admin', 'superadmin'].includes(normalizeRole(data.role || data.userRole)) || data.isBlocked === true) {
    throw new Error('Only Super Admin can manage platform employees.');
  }
  return uid;
};

const assertReference = async (collectionName, id, label) => {
  if (!id) return;
  const snap = await getDoc(doc(db, collectionName, id));
  if (!snap.exists()) throw new Error(`${label} no longer exists. Refresh and choose another ${label.toLowerCase()}.`);
  if (snap.data()?.isActive === false) throw new Error(`${label} is disabled. Choose an active ${label.toLowerCase()}.`);
};

const assertUniqueName = async (collectionName, name, currentId, label) => {
  const normalized = cleanString(name).toLowerCase();
  if (!normalized) return;
  const snap = await getDocs(collection(db, collectionName));
  const duplicate = snap.docs.some((row) => row.id !== currentId && cleanString(row.data()?.name).toLowerCase() === normalized && row.data()?.isActive !== false);
  if (duplicate) throw new Error(`${label} name already exists.`);
};


const assertNoManagerCycle = async (employeeId, managerId) => {
  if (!employeeId || !managerId) return;
  const visited = new Set([employeeId]);
  let cursor = managerId;
  for (let depth = 0; cursor && depth < 60; depth += 1) {
    if (visited.has(cursor)) throw new Error('Reporting manager selection would create a reporting cycle.');
    visited.add(cursor);
    const snap = await getDoc(doc(db, EMPLOYEES, cursor));
    if (!snap.exists()) break;
    cursor = cleanString(snap.data()?.reportingManagerId);
  }
  if (cursor) throw new Error('Reporting structure is too deep. Review the manager hierarchy before saving.');
};

const assertUniqueEmployeeId = async (employeeId, currentId = '') => {
  const value = cleanString(employeeId);
  if (!value) return;
  const snap = await getDocs(query(collection(db, EMPLOYEES), where('employeeId', '==', value)));
  if (snap.docs.some((row) => row.id !== currentId)) throw new Error('Employee ID already exists.');
};

const sanitizeEmployee = (employee = {}) => ({
  firstName: cleanString(employee.firstName),
  lastName: cleanString(employee.lastName),
  displayName: cleanString(employee.displayName || `${employee.firstName || ''} ${employee.lastName || ''}`),
  phone: cleanString(employee.phone),
  departmentId: cleanString(employee.departmentId),
  designation: cleanString(employee.designation),
  roleId: cleanString(employee.roleId),
  reportingManagerId: cleanString(employee.reportingManagerId),
  employmentType: cleanString(employee.employmentType || 'full_time'),
  joiningDate: cleanString(employee.joiningDate),
  location: cleanString(employee.location),
  skills: Array.isArray(employee.skills)
    ? employee.skills.map((value) => cleanString(value)).filter(Boolean)
    : cleanString(employee.skills).split(',').map((value) => value.trim()).filter(Boolean),
  status: ['active', 'on_leave', 'inactive'].includes(String(employee.status || 'active')) ? String(employee.status || 'active') : 'active',
});

export async function listEmployees() {
  await ensureAdmin();
  const snap = await getDocs(collection(db, EMPLOYEES));
  return snap.docs
    .map((row) => ({ id: row.id, ...row.data() }))
    .sort((a, b) => String(a.displayName || a.email || '').localeCompare(String(b.displayName || b.email || '')));
}

export async function listEmployeeRoles() {
  await ensureAdmin();
  const snap = await getDocs(collection(db, ROLES));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() })).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

export async function listDepartments() {
  await ensureAdmin();
  const snap = await getDocs(collection(db, DEPARTMENTS));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() })).sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}


const assertNoActiveAssignments = async (field, id, label) => {
  if (!id) return;
  const snap = await getDocs(query(collection(db, EMPLOYEES), where(field, '==', id)));
  const active = snap.docs.find((row) => row.data()?.isActive !== false && row.data()?.status !== 'inactive');
  if (active) throw new Error(`${label} is assigned to an active employee. Reassign active employees before disabling it.`);
};

export async function saveEmployeeRole(role) {
  const uid = await ensureAdmin();
  const ref = role.id ? doc(db, ROLES, role.id) : doc(collection(db, ROLES));
  const payload = {
    name: cleanString(role.name),
    description: cleanString(role.description),
    permissions: [...new Set(Array.isArray(role.permissions) ? role.permissions.map(cleanString).filter(Boolean) : [])],
    isActive: role.isActive !== false,
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (!payload.name) throw new Error('Role name is required.');
  await assertUniqueName(ROLES, payload.name, role.id, 'Role');
  if (role.id && payload.isActive === false) await assertNoActiveAssignments('roleId', role.id, 'Role');
  await setDoc(ref, { ...payload, createdAt: role.createdAt || serverTimestamp(), ...(payload.isActive ? { deletedAt: null } : {}) }, { merge: true });
  return { id: ref.id, ...payload };
}

export async function deleteEmployeeRole(id) {
  await ensureAdmin();
  await assertNoActiveAssignments('roleId', id, 'Role');
  await setDoc(doc(db, ROLES, id), { isActive: false, deletedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
}

export async function saveDepartment(department) {
  const uid = await ensureAdmin();
  const ref = department.id ? doc(db, DEPARTMENTS, department.id) : doc(collection(db, DEPARTMENTS));
  if (department.managerId) await assertReference(EMPLOYEES, department.managerId, 'Manager');
  const payload = {
    name: cleanString(department.name),
    description: cleanString(department.description),
    managerId: cleanString(department.managerId),
    isActive: department.isActive !== false,
    updatedAt: serverTimestamp(),
    updatedBy: uid,
  };
  if (!payload.name) throw new Error('Department name is required.');
  await assertUniqueName(DEPARTMENTS, payload.name, department.id, 'Department');
  if (department.id && payload.isActive === false) await assertNoActiveAssignments('departmentId', department.id, 'Department');
  await setDoc(ref, { ...payload, createdAt: department.createdAt || serverTimestamp(), ...(payload.isActive ? { deletedAt: null } : {}) }, { merge: true });
  return { id: ref.id, ...payload };
}

export async function deleteDepartment(id) {
  await ensureAdmin();
  await assertNoActiveAssignments('departmentId', id, 'Department');
  await setDoc(doc(db, DEPARTMENTS, id), { isActive: false, deletedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
}

export async function createEmployee(employee) {
  const actor = await ensureAdmin();
  const email = cleanString(employee.email).toLowerCase();
  const password = String(employee.password || '');
  const clean = sanitizeEmployee(employee);
  if (!email) throw new Error('Employee email is required.');
  if (password.length < 8) throw new Error('Temporary password must contain at least 8 characters.');
  if (!clean.displayName) throw new Error('Employee name is required.');
  const requestedEmployeeId = cleanString(employee.employeeId);
  if (requestedEmployeeId) await assertUniqueEmployeeId(requestedEmployeeId);
  await Promise.all([
    assertReference(ROLES, clean.roleId, 'Role'),
    assertReference(DEPARTMENTS, clean.departmentId, 'Department'),
    assertReference(EMPLOYEES, clean.reportingManagerId, 'Reporting manager'),
  ]);

  const secondary = initializeApp(app.options, `employee-create-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
  const secondaryAuth = getAuth(secondary);
  let createdUser = null;
  try {
    const credential = await createUserWithEmailAndPassword(secondaryAuth, email, password);
    createdUser = credential.user;
    const id = createdUser.uid;
    const payload = {
      ...clean,
      employeeId: cleanString(employee.employeeId) || `TF-${Date.now().toString().slice(-7)}`,
      email,
      status: clean.status,
      isActive: clean.status !== 'inactive',
      isBlocked: clean.status === 'inactive',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: actor,
    };
    const batch = writeBatch(db);
    batch.set(doc(db, EMPLOYEES, id), payload, { merge: true });
    batch.set(doc(db, 'users', id), {
      uid: id,
      email,
      displayName: payload.displayName,
      firstName: payload.firstName,
      lastName: payload.lastName,
      role: 'platform_employee',
      userRole: 'platform_employee',
      employeeId: payload.employeeId,
      employeeRoleId: payload.roleId,
      departmentId: payload.departmentId,
      isActive: payload.isActive,
      isBlocked: payload.isBlocked,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await batch.commit();
    await signOut(secondaryAuth).catch(() => {});
    return { id, ...payload };
  } catch (error) {
    if (createdUser) await deleteUser(createdUser).catch(() => {});
    throw error;
  } finally {
    await deleteApp(secondary).catch(() => {});
  }
}

export async function updateEmployee(id, employee) {
  const uid = await ensureAdmin();
  const current = await getDoc(doc(db, EMPLOYEES, id));
  if (!current.exists()) throw new Error('Employee record was not found.');
  const clean = sanitizeEmployee(employee);
  if (!clean.displayName) throw new Error('Employee name is required.');
  if (clean.reportingManagerId === id) throw new Error('An employee cannot report to themselves.');
  await assertNoManagerCycle(id, clean.reportingManagerId);
  if (employee.employeeId) await assertUniqueEmployeeId(employee.employeeId, id);
  await Promise.all([
    assertReference(ROLES, clean.roleId, 'Role'),
    assertReference(DEPARTMENTS, clean.departmentId, 'Department'),
    assertReference(EMPLOYEES, clean.reportingManagerId, 'Reporting manager'),
  ]);
  const isActive = clean.status !== 'inactive';
  const payload = { ...clean, isActive, isBlocked: !isActive, updatedAt: serverTimestamp(), updatedBy: uid };
  const batch = writeBatch(db);
  if (!isActive) {
    const [reports, managedDepartments] = await Promise.all([
      getDocs(query(collection(db, EMPLOYEES), where('reportingManagerId', '==', id))),
      getDocs(query(collection(db, DEPARTMENTS), where('managerId', '==', id))),
    ]);
    if (reports.size + managedDepartments.size > 450) throw new Error('This employee has too many reporting references to clear automatically. Reassign them before setting the employee inactive.');
    reports.docs.forEach((row) => batch.set(row.ref, { reportingManagerId: '', updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true }));
    managedDepartments.docs.forEach((row) => batch.set(row.ref, { managerId: '', updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true }));
  }
  batch.set(doc(db, EMPLOYEES, id), payload, { merge: true });
  batch.set(doc(db, 'users', id), {
    displayName: clean.displayName,
    firstName: clean.firstName,
    lastName: clean.lastName,
    employeeRoleId: clean.roleId,
    departmentId: clean.departmentId,
    isActive,
    isBlocked: !isActive,
    updatedAt: serverTimestamp(),
    ...(isActive ? { employmentEndedAt: null } : { employmentEndedAt: serverTimestamp() }),
  }, { merge: true });
  await batch.commit();
  return { success: true };
}

// Historical employee data is retained. This action disables login/access rather than deleting the record.
export async function deleteEmployee(id) {
  const uid = await ensureAdmin();
  const current = await getDoc(doc(db, EMPLOYEES, id));
  if (!current.exists()) throw new Error('Employee record was not found.');
  const [reports, managedDepartments] = await Promise.all([
    getDocs(query(collection(db, EMPLOYEES), where('reportingManagerId', '==', id))),
    getDocs(query(collection(db, DEPARTMENTS), where('managerId', '==', id))),
  ]);
  if (reports.size + managedDepartments.size > 450) throw new Error('This employee has too many reporting references to clear automatically. Reassign them before disabling access.');
  const batch = writeBatch(db);
  reports.docs.forEach((row) => batch.set(row.ref, { reportingManagerId: '', updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true }));
  managedDepartments.docs.forEach((row) => batch.set(row.ref, { managerId: '', updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true }));
  batch.set(doc(db, EMPLOYEES, id), { status: 'inactive', isActive: false, isBlocked: true, deactivatedAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedBy: uid }, { merge: true });
  batch.set(doc(db, 'users', id), { isActive: false, isBlocked: true, employmentEndedAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
  await batch.commit();
  return { success: true };
}

export const PERMISSION_GROUPS = {
  dashboard: ['dashboard.read'],
  employees: ['employees.read', 'employees.create', 'employees.update', 'employees.delete', 'employees.roles.manage'],
  crm: ['crm.read', 'crm.create', 'crm.update', 'crm.delete', 'crm.assign', 'crm.export'],
  meetings: ['employee_meetings.read', 'employee_meetings.create', 'employee_meetings.host', 'employee_meetings.delete'],
  reports: ['reports.read', 'reports.export'],
  companies: ['companies.read', 'companies.update'],
  support: ['support.read', 'support.manage'],
};

export default {
  listEmployees,
  listEmployeeRoles,
  listDepartments,
  saveEmployeeRole,
  deleteEmployeeRole,
  saveDepartment,
  deleteDepartment,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  PERMISSION_GROUPS,
};
